import * as React from 'react';
import { useState, useRef } from 'react';
import { TooltipProps } from './TooltipTypes'
import { Typography } from '../../foundation/typography';
import { CSSTransition } from 'react-transition-group';
import { usePopper } from 'react-popper';
import { useTooltipTheme } from '../../../themes';

export const Tooltip = ({
    children,
    linkOnClick,
    linkText,
    offset,
    open,
    placement = 'top',
    text = "",
    tooltipId,
    variant = 'primary',
}: TooltipProps) => {
    const { tooltipTheme } = useTooltipTheme();

    const [isOpen, setIsOpen] = useState(false);
    const referenceElement = useRef(null);
    const popperElement = useRef(null);
    const isAutoPlacement = placement === 'auto' ? true : false;
    const { styles, attributes, update } = usePopper(referenceElement.current, popperElement.current, {
        //Set placement to 'bottom' when auto placement to follow fallbackPlacements order
        placement: isAutoPlacement ? 'bottom' : placement, 
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: offset ? offset : [0, 10]
                }
            },
            // Set Flip modifier only at auto-placement.
            {
                name: 'flip',
                enabled: isAutoPlacement,
                options: {
                    allowedAutoPlacements: ['bottom', 'top', 'right', 'left'],
                    fallbackPlacements: ['bottom', 'top', 'right', 'left']
                }
            }
        ]
    });

    // If open is not manually managed, than use default hover focus triggers
    const tooltipIsOpen = open !== undefined ? open : isOpen;

    // Sets tooltip open state based on hover focus triggers
    const setTooltipOpen = (setOpen: boolean) => {
        if(setOpen) {
            update && update();
            text && setIsOpen(true);
        } else {
            text && setIsOpen(false)
        }
    }

    // Incase of auto placement, get dynamic placement of tooltip from usePopper hook.
    const flipPlacement = attributes.popper?.['data-popper-placement'].toString();

    const tooltipBodyColor = (`${variant}` === 'spotlight') ? '$color-secondary-a1' : '$color-primary-c1';

    // Classes
    const tooltipWrapperClass = tooltipTheme[`bsw-tooltip--${variant}`];
    const tooltipPositionClass = placement === 'auto'
        ? tooltipTheme[`bsw-tooltip-${flipPlacement}`]
        : tooltipTheme[`bsw-tooltip-${placement}`];
    const tooltipClasses = [
        tooltipTheme['bsw-tooltip'], 
        tooltipIsOpen ? '' : tooltipTheme['bsw-tooltip-hidden'],
        tooltipPositionClass
    ].join(' ');

    return (
        <div ref={referenceElement}
            className={tooltipWrapperClass}
        >
            <div 
                onMouseEnter={() => setTooltipOpen(true)}
                onMouseLeave={() => setTooltipOpen(false)}
                onFocus={() => setTooltipOpen(true)}
                onBlur={()  => setTooltipOpen(false)}
                onKeyDown={(e) => e.key.toLowerCase() == "escape" && e.target instanceof HTMLElement && (e.target as HTMLElement).blur()}
            >
                {children}
            </div>
            <CSSTransition in={tooltipIsOpen} timeout={0} 
                classNames={{
                    enterActive: tooltipTheme['fade-enter'],
                    enterDone: tooltipTheme['fade-enter-done'],
                    exitActive: tooltipTheme['fade-exit'],
                    exitDone: tooltipTheme['fade-exit-done']
                  }}
                >
                <div ref={popperElement}
                    {...attributes.popper}
                    style={styles.popper}
                    className={tooltipClasses}
                    id={tooltipId ? tooltipId : undefined}
                    role="tooltip"
                >
                    <div className={tooltipTheme['bsw-tooltip-arrow']}></div>
                    <div className={tooltipTheme['bsw-tooltip-content']}>
                        <Typography
                            color={tooltipBodyColor}
                            tag="div"
                            textAlign="center"
                            type="meta-01"
                        >
                            {text}
                        </Typography>
                        {linkText && linkOnClick &&
                            <a
                                className={tooltipTheme['bsw-tooltip-link']}
                                href="#"
                                onClick={(e) => { e.preventDefault(); linkOnClick(e) }}
                                role="button"
                            >
                                <Typography bold>{linkText}</Typography>
                            </a>
                        }
                    </div>
                </div>
            </CSSTransition>
        </div>
    )
}